/**
 * User Service
 * Centralizes all user-related API operations including profile, pictures, and development tools
 */

import apiService from './api';

// ============ Profile Picture Operations ============

/**
 * Upload a new profile picture
 * @param {File|Blob} imageFile - Image file to upload
 * @returns {Promise<Object>} Upload result with new profile picture info
 */
const uploadProfilePicture = async (imageFile) => {
  const formData = new FormData();
  formData.append('profilePicture', imageFile, 'profile-picture.jpg');
  
  // No need to set Content-Type - api.js will handle FormData correctly
  const response = await apiService.post('/api/profile-picture/upload', formData);
  
  // Parse JSON once here and return the data along with response status
  const data = await response.json();
  return { ok: response.ok, data };
};

/**
 * Delete user's profile picture
 * @returns {Promise<Object>} Delete operation result
 */
const deleteProfilePicture = async () => {
  const response = await apiService.delete('/api/profile-picture');
  const data = await response.json();
  return { ok: response.ok, data };
};

// ============ Time Information ============

/**
 * Get server time information for daily reset calculations
 * @returns {Promise<Object>} Time info including next reset time
 */
const getTimeInfo = async () => {
  const response = await apiService.get('/api/time/info');
  return response.json();
};

// ============ Development Tools (Dev Environment Only) ============

/**
 * Get all user data for development/debugging purposes
 * @returns {Promise<Object>} Complete user data dump
 */
const getMyDevData = async () => {
  const response = await apiService.get('/api/dev/my-data');
  return response.json();
};

/**
 * Delete user account (development tool)
 * @returns {Promise<Object>} Delete operation result
 */
const deleteMyAccount = async () => {
  const response = await apiService.delete('/api/dev/delete-my-account');
  return response.json();
};

// ============ Public Profile Operations ============

/**
 * Get public profile information for a user
 * @param {string} username - Username to get profile for
 * @returns {Promise<Object>} Public profile data
 */
const getPublicProfile = async (username) => {
  const response = await apiService.get(`/auth/users/profile/${username}`);
  return response.json();
};

/**
 * Get full public profile data (profile + achievements) in a single request
 * @param {string} username - Username to get full profile for
 * @returns {Promise<Object>} Full public profile with achievements
 */
const getFullPublicProfile = async (username) => {
  const response = await apiService.get(`/auth/public-profile/${username}`);
  return response.json();
};

/**
 * Update user's display name or other profile info
 * @param {Object} profileData - Profile data to update
 * @param {string} [profileData.display_name] - Display name
 * @param {string} [profileData.username] - Username (if allowed to change)
 * @returns {Promise<Object>} Updated profile data
 */
const updateProfile = async (profileData) => {
  const response = await apiService.put('/auth/profile', profileData);
  return response.json();
};

// ============ User Activities & Tracking ============

/**
 * Track user activity (e.g., viewing How to Play guides)
 * @param {Object} activityData - Activity data to track
 * @param {string} activityData.activityType - Type of activity
 * @param {string} [activityData.gameId] - Game ID if activity is game-related
 * @param {Object} [activityData.metadata] - Additional activity metadata
 * @returns {Promise<Object>} Activity tracking result
 */
const trackActivity = async (activityData) => {
  const response = await apiService.post('/api/user-activities', activityData);
  return response.json();
};

/**
 * Get user activity history
 * @param {Object} [params] - Query parameters
 * @param {number} [params.limit] - Number of activities to return
 * @param {string} [params.activityType] - Filter by activity type
 * @returns {Promise<Array>} User activity history
 */
const getUserActivities = async (params = {}) => {
  const searchParams = new URLSearchParams(
    Object.entries(params).filter(([_, value]) => value !== '' && value != null)
  );
  const response = await apiService.get(`/api/user-activities?${searchParams}`);
  return response.json();
};

// ============ Leaderboard ============

/**
 * Get leaderboard data
 * @param {Object} [params] - Query parameters
 * @param {string} [params.game] - Specific game leaderboard
 * @param {string} [params.type] - Leaderboard type (current-streak, best-streak, total-wins)
 * @param {number} [params.limit] - Number of entries to return
 * @returns {Promise<Array>} Leaderboard data
 */
const getLeaderboard = async (params = {}) => {
  const searchParams = new URLSearchParams(
    Object.entries(params).filter(([_, value]) => value !== '' && value != null)
  );
  const response = await apiService.get(`/api/leaderboard?${searchParams}`);
  return response.json();
};

export default {
  // Profile Pictures
  uploadProfilePicture,
  deleteProfilePicture,
  
  // Time Info
  getTimeInfo,
  
  // Development Tools
  getMyDevData,
  deleteMyAccount,
  
  // Public Profile
  getPublicProfile,
  getFullPublicProfile,
  updateProfile,
  
  // Activities
  trackActivity,
  getUserActivities,
  
  // Leaderboard
  getLeaderboard
};